// "Notify me when in stock" popup for sold-out product cards
// (button markup: snippets/product-card.liquid).
//
// Guests: step 1 asks for an email, step 2 confirms. Signed-in customers:
// subscribed straight away with their account email, popup opens on step 2.
// Step 2 also shows "people who buy X also buy" cross-sells from Shopify's
// product recommendations. Subscriptions go to Klaviyo's public back-in-stock
// API, so interest is recorded per product in Klaviyo ("Subscribed to Back in
// Stock") and its Back in Stock flow can email people once it's restocked.
// Uses event delegation, so cards the shop page loads later via fetch work.
(() => {
  if (window.ppCardNotifyReady) return;
  window.ppCardNotifyReady = true;

  const CROSS_SELL_LIMIT = 4;
  const EXCLUDED_HANDLES = ['puffy-prime'];

  const subscribe = (companyId, variantId, email) => fetch(
    `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: '2025-07-15',
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: { data: { type: 'profile', attributes: { email } } },
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
          },
        },
      }),
    }
  ).then((response) => {
    if (response.ok) return;
    return response.json().catch(() => ({})).then((body) => {
      const detail = body && body.errors && body.errors[0] && body.errors[0].detail;
      const error = new Error(detail || `Back-in-stock signup failed (${response.status})`);
      error.invalidEmail = /email/i.test(detail || '');
      throw error;
    });
  });

  const rootURL = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const formatMoney = (cents) => {
    const theme = window.theme;
    if (theme && theme.Currency && typeof theme.Currency.formatMoney === 'function' && theme.settings) {
      try {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      } catch (error) {
        // fall through to Intl
      }
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);
  };

  const imageURL = (src, width) => {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const fetchCrossSells = (productId) => fetch(
    `${rootURL()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`
  )
    .then((response) => (response.ok ? response.json() : { products: [] }))
    .then((data) => (data.products || [])
      .filter((product) => product.available && !EXCLUDED_HANDLES.includes(product.handle))
      .slice(0, CROSS_SELL_LIMIT))
    .catch(() => []);

  // ---- Popup -------------------------------------------------------------

  let dialog = null;
  let current = null;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  const buildDialog = () => {
    const d = el('dialog', 'pp-notify');
    d.setAttribute('aria-labelledby', 'PPNotifyHeading');
    d.innerHTML = `
      <div class="pp-notify__panel">
        <button type="button" class="pp-notify__close" data-pp-notify-close aria-label="Close">&times;</button>

        <div class="pp-notify__step" data-pp-notify-step="email">
          <p class="pp-notify__eyebrow" data-pp-notify-product></p>
          <h2 class="pp-notify__heading" id="PPNotifyHeading">Please let us know where we can reach you</h2>
          <p class="pp-notify__text">We'll send one email when it's back in stock.</p>
          <form class="pp-notify__form" data-pp-notify-form novalidate>
            <label class="pp-notify__sr" for="PPNotifyEmail">Email address</label>
            <input class="pp-notify__input" id="PPNotifyEmail" type="email" name="email" autocomplete="email" placeholder="Your email" required>
            <button type="submit" class="pp-notify__submit">Notify me</button>
          </form>
          <p class="pp-notify__error" data-pp-notify-error role="alert" hidden></p>
        </div>

        <div class="pp-notify__step" data-pp-notify-step="loading" hidden>
          <p class="pp-notify__text">Signing you up…</p>
        </div>

        <div class="pp-notify__step" data-pp-notify-step="done" hidden>
          <h2 class="pp-notify__heading" data-pp-notify-done-heading tabindex="-1"></h2>
          <p class="pp-notify__text" data-pp-notify-done-text></p>
          <div class="pp-notify__cross" data-pp-notify-cross hidden>
            <p class="pp-notify__cross-heading" data-pp-notify-cross-heading></p>
            <ul class="pp-notify__cards" data-pp-notify-cards></ul>
          </div>
        </div>
      </div>`;

    d.addEventListener('click', (event) => {
      // Click on the backdrop (outside the panel) or the close button closes it.
      if (event.target === d || event.target.closest('[data-pp-notify-close]')) d.close();
    });
    d.querySelector('[data-pp-notify-form]').addEventListener('submit', (event) => {
      event.preventDefault();
      submitGuestEmail();
    });
    d.addEventListener('close', () => {
      document.documentElement.classList.remove('pp-notify-open');
      if (current && current.trigger && document.contains(current.trigger)) current.trigger.focus();
      current = null;
    });
    document.body.appendChild(d);
    return d;
  };

  const showStep = (name) => {
    dialog.querySelectorAll('[data-pp-notify-step]').forEach((step) => {
      step.hidden = step.dataset.ppNotifyStep !== name;
    });
  };

  const setError = (text) => {
    const error = dialog.querySelector('[data-pp-notify-error]');
    error.textContent = text || '';
    error.hidden = !text;
  };

  const renderCrossSells = (products, productTitle) => {
    const wrap = dialog.querySelector('[data-pp-notify-cross]');
    const list = dialog.querySelector('[data-pp-notify-cards]');
    list.textContent = '';
    if (products.length === 0) {
      wrap.hidden = true;
      return;
    }

    dialog.querySelector('[data-pp-notify-cross-heading]').textContent = `People who buy ${productTitle} also buy`;
    products.forEach((product) => {
      const item = el('li', 'pp-notify__card');
      const link = el('a', 'pp-notify__card-link');
      link.href = product.url;

      const media = el('span', 'pp-notify__card-media');
      if (product.featured_image) {
        const img = el('img');
        img.src = imageURL(product.featured_image, 360);
        img.alt = product.title;
        img.loading = 'lazy';
        img.width = 180;
        img.height = 180;
        media.appendChild(img);
      }

      const title = el('span', 'pp-notify__card-title', product.title);
      const price = el('span', 'pp-notify__card-price');
      price.appendChild(el('span', '', formatMoney(product.price)));
      if (product.compare_at_price && product.compare_at_price > product.price) {
        price.appendChild(el('s', 'pp-notify__card-compare', formatMoney(product.compare_at_price)));
      }

      link.append(media, title, price);
      item.appendChild(link);
      list.appendChild(item);
    });
    wrap.hidden = false;
  };

  const showDone = () => {
    dialog.querySelector('[data-pp-notify-done-heading]').textContent = "You're on the list!";
    dialog.querySelector('[data-pp-notify-done-text]').textContent =
      `We'll notify you when ${current.productTitle} is back in stock.`;
    showStep('done');
    const heading = dialog.querySelector('[data-pp-notify-done-heading]');
    if (heading) heading.focus();
  };

  const doSubscribe = (email) => {
    const request = current;
    showStep('loading');
    return subscribe(request.companyId, request.variantId, email)
      .then(() => {
        if (current !== request) return;
        showDone();
      })
      .catch((error) => {
        if (current !== request) return;
        if (window.console) window.console.error(error);
        showStep('email');
        setError(error.invalidEmail ? 'Please enter a valid email.' : 'Something went wrong. Please try again.');
      });
  };

  const submitGuestEmail = () => {
    if (!current) return;
    const input = dialog.querySelector('#PPNotifyEmail');
    const email = input.value.trim();
    if (!email || !input.checkValidity()) {
      setError('Please enter a valid email.');
      input.focus();
      return;
    }
    setError('');
    doSubscribe(email);
  };

  const open = (trigger) => {
    if (!dialog) dialog = buildDialog();
    if (dialog.open) dialog.close();

    current = {
      trigger,
      productId: trigger.dataset.productId,
      productTitle: trigger.dataset.productTitle || 'this product',
      variantId: trigger.dataset.variantId,
      companyId: trigger.dataset.klaviyoCompanyId,
    };

    setError('');
    dialog.querySelector('[data-pp-notify-product]').textContent = current.productTitle;
    dialog.querySelector('[data-pp-notify-cross]').hidden = true;
    const request = current;
    fetchCrossSells(current.productId).then((products) => {
      if (current === request) renderCrossSells(products, request.productTitle);
    });

    document.documentElement.classList.add('pp-notify-open');
    dialog.showModal();

    const customerEmail = trigger.dataset.customerEmail;
    dialog.querySelector('#PPNotifyEmail').value = customerEmail || '';
    if (customerEmail) {
      // Signed in: use the account email, no form needed.
      doSubscribe(customerEmail);
    } else {
      showStep('email');
      const input = dialog.querySelector('#PPNotifyEmail');
      input.focus();
    }
  };

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-card-notify]');
    if (!trigger) return;
    event.preventDefault();
    open(trigger);
  });
})();
